<template>
  <div class="search-preferences-container pref-root">
    
    <!-- =============================================================
         LAYOUT 1: Register Flow (5-Step Ultra-Clean Wizard)
         ============================================================= -->
    <div v-if="isFromRegister" class="wizard-theme-wrapper d-flex justify-content-center align-items-center text-start">
      
      <div class="register-step-card-cozy p-3.5 p-sm-4 p-md-5 rounded-4 shadow-sm animate-scale-up position-relative overflow-hidden" style="max-width: 700px; width: 100%;">
        
        <!-- Header Section -->
        <div class="mb-4 position-relative d-flex justify-content-between align-items-start">
          <div class="text-start">
            <div class="d-flex align-items-center gap-2 mb-2">
              <span class="badge-step-indicator-cozy font-mono">BƯỚC {{ currentStep }} / 3</span>
              <span class="badge-registration-cozy">KHÁM PHÁ SÁCH HAY</span>
            </div>
            <h2 class="h3 text-dark-cozy fw-bold font-serif mb-1 tracking-wide">
              <span v-if="currentStep === 1">Tâm trạng & Suy nghĩ hôm nay 🌀</span>
              <span v-else-if="currentStep === 2">Phong cách & Mục tiêu đọc sách 🏆</span>
              <span v-else-if="currentStep === 3">Thể loại sách bạn quan tâm 📚</span>
            </h2>
            <p class="text-muted-cozy fs-7-5 mb-0">
              <span v-if="currentStep === 1">Viết một vài tâm tư và chọn cảm xúc hiện tại để hệ thống đồng điệu với bạn.</span>
              <span v-else-if="currentStep === 2">Thiết lập gu đọc và mục đích của bạn giúp gợi ý chính xác hơn.</span>
              <span v-else-if="currentStep === 3">Chọn nhiều nhất 3 thể loại sách bạn muốn tìm tòi vào lúc này nhé.</span>
            </p>
          </div>
          
          <!-- Top Skip Button -->
          <button 
            type="button" 
            class="btn-skip-top-cozy text-decoration-none" 
            @click="skipUpdate"
            :disabled="isLoading"
          >
            <span>Bỏ qua</span>
            <icon icon="fa-solid fa-chevron-right" class="fs-8" />
          </button>
        </div>

        <!-- Segmented Progress Bar Cozy -->
        <div class="cozy-progress-bar-container d-flex gap-2 mb-5">
          <div 
            v-for="step in 3" 
            :key="step" 
            class="cozy-progress-segment flex-grow-1" 
            :class="{ 'active': currentStep >= step, 'current': currentStep === step }"
          ></div>
        </div>

        <!-- Form content -->
        <form @submit.prevent="handleSave" class="position-relative">
          
          <!-- Step 1: FreeText + Mood Bento -->
          <div v-if="currentStep === 1" class="custom-form-group animate-fade-in d-flex flex-column gap-4">
            <!-- FreeText Thoughts -->
            <div class="cozy-form-block">
              <label class="form-label font-serif fw-bold text-dark-cozy mb-2 fs-7-5">Hôm nay bạn đang nghĩ gì? ✍️</label>
              <div class="pref-freetext-wrapper-cozy">
                <textarea
                  v-model="prefFreeText"
                  class="pref-freetext-area-cozy"
                  placeholder="Ví dụ: Mình đang muốn tìm sách trinh thám hồi hộp, hoặc sách self-help nhẹ nhàng..."
                  rows="3"
                  maxlength="300"
                  @input="autoResize"
                  ref="freetextArea"
                ></textarea>
                <div class="pref-freetext-footer-cozy">
                  <span class="pref-freetext-hint-cozy"><icon icon="fa-solid fa-circle-info" /> Giúp hệ thống gợi ý trúng gu hơn</span>
                  <span class="pref-freetext-count-cozy">{{ prefFreeText.length }}/300</span>
                </div>
              </div>
            </div>

            <!-- Mood Selection -->
            <div class="cozy-form-block">
              <label class="form-label font-serif fw-bold text-dark-cozy mb-2 fs-7-5">Tâm trạng hiện tại của bạn?</label>
              <div class="row g-2">
                <div v-for="opt in moodOptions" :key="opt.value" class="col-sm-6 col-12">
                  <label 
                    class="pref-card-label-cozy d-flex align-items-center justify-content-between p-2.5 rounded-4 text-start position-relative"
                    :class="{ 'active': prefMood === opt.value }"
                    style="cursor: pointer;"
                  >
                    <input type="radio" :value="opt.value" v-model="prefMood" class="d-none" />
                    <div class="d-flex align-items-center gap-2" style="flex: 1; min-width: 0;">
                      <div class="card-emoji-box-cozy">{{ opt.emoji }}</div>
                      <span class="fw-bold fs-8 text-dark-cozy select-title">{{ opt.label }}</span>
                    </div>
                    <icon v-if="prefMood === opt.value" icon="fa-solid fa-circle-check" class="check-icon-cozy" />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Book Style & Goal Bento -->
          <div v-if="currentStep === 2" class="custom-form-group animate-fade-in d-flex flex-column gap-4">
            <!-- Book Style -->
            <div class="cozy-form-block">
              <label class="form-label font-serif fw-bold text-dark-cozy mb-2 fs-7-5">Phong cách đọc bạn thích?</label>
              <div class="row g-2">
                <div v-for="opt in bookTypeOptions" :key="opt.value" class="col-sm-6 col-12">
                  <label 
                    class="pref-card-label-cozy d-flex align-items-center justify-content-between p-2.5 rounded-4 text-start position-relative"
                    :class="{ 'active': prefBookType === opt.value }"
                    style="cursor: pointer;"
                  >
                    <input type="radio" :value="opt.value" v-model="prefBookType" class="d-none" />
                    <div class="d-flex align-items-center gap-2" style="flex: 1; min-width: 0;">
                      <div class="card-emoji-box-cozy">{{ opt.emoji }}</div>
                      <span class="fw-bold fs-8 text-dark-cozy select-title">{{ opt.label }}</span>
                    </div>
                    <icon v-if="prefBookType === opt.value" icon="fa-solid fa-circle-check" class="check-icon-cozy" />
                  </label>
                </div>
              </div>
            </div>

            <!-- Goal Selection -->
            <div class="cozy-form-block">
              <label class="form-label font-serif fw-bold text-dark-cozy mb-2 fs-7-5">Mục tiêu hướng tới?</label>
              <div class="row g-2">
                <div v-for="opt in goalOptions" :key="opt.value" class="col-sm-6 col-12">
                  <label 
                    class="pref-card-label-cozy d-flex align-items-center justify-content-between p-2.5 rounded-4 text-start position-relative"
                    :class="{ 'active': prefGoal === opt.value }"
                    style="cursor: pointer;"
                  >
                    <input type="radio" :value="opt.value" v-model="prefGoal" class="d-none" />
                    <div class="d-flex align-items-center gap-2" style="flex: 1; min-width: 0;">
                      <div class="card-emoji-box-cozy">{{ opt.emoji }}</div>
                      <span class="fw-bold fs-8 text-dark-cozy select-title">{{ opt.label }}</span>
                    </div>
                    <icon v-if="prefGoal === opt.value" icon="fa-solid fa-circle-check" class="check-icon-cozy" />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 3: Genre Checkboxes (Pills style) -->
          <div v-if="currentStep === 3" class="custom-form-group animate-fade-in">
            <div class="genre-counter-info mb-3 d-flex justify-content-between align-items-center">
              <span class="fs-8 text-muted-cozy">Chọn những thể loại bạn quan tâm (tối đa 3):</span>
              <span class="badge-genre-counter-cozy">{{ prefGenres.length }} / 3</span>
            </div>
            <!-- Search categories input -->
            <div class="mb-3 genre-search-wrapper cozy-search">
              <input 
                v-model="searchGenreQuery" 
                type="text" 
                class="genre-search-input-cozy" 
                placeholder="Tìm kiếm thể loại..."
              />
              <icon icon="fa-solid fa-magnifying-glass" class="genre-search-icon-left" />
              <button
                v-if="searchGenreQuery"
                type="button"
                class="genre-search-btn-clear"
                @click="searchGenreQuery = ''"
              >
                <icon icon="fa-solid fa-xmark" />
              </button>
            </div>
            <div class="pref-genre-pills-container d-flex flex-wrap gap-2 max-h-350-scroller">
              <label 
                v-for="opt in filteredGenreOptions" 
                :key="opt.value"
                class="pref-genre-pill-v2 cozy-pill d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill text-start position-relative"
                :class="{ 
                  'active': prefGenres.includes(opt.value), 
                  'disabled-pill': prefGenres.length >= 3 && !prefGenres.includes(opt.value) 
                }"
                style="cursor: pointer;"
              >
                <input 
                  type="checkbox" 
                  :value="opt.value" 
                  v-model="prefGenres" 
                  class="d-none"
                  :disabled="prefGenres.length >= 3 && !prefGenres.includes(opt.value)"
                />
                <icon :icon="opt.icon" class="pill-icon" :class="prefGenres.includes(opt.value) ? 'text-white' : 'text-primary'" />
                <span class="fs-8 select-title text-dark-cozy">{{ opt.label }}</span>
                <icon v-if="prefGenres.includes(opt.value)" icon="fa-solid fa-circle-check" class="pill-check" />
              </label>
            </div>
          </div>

          <!-- Wizard Navigation Footer -->
          <div class="d-flex justify-content-between align-items-center mt-5 pt-3 border-top-cozy">
            <div>
              <button 
                v-if="currentStep > 1"
                type="button" 
                class="btn-wizard-nav-back-cozy"
                @click="prevStep"
                :disabled="isLoading"
              >
                <icon icon="fa-solid fa-arrow-left" class="me-2" />
                <span>Quay lại</span>
              </button>
            </div>
            
            <div>
              <button 
                v-if="currentStep < 3"
                type="button" 
                class="btn-wizard-nav-next-cozy"
                @click="nextStep"
                :disabled="isLoading"
              >
                <span>Tiếp tục</span>
                <icon icon="fa-solid fa-arrow-right" class="ms-2" />
              </button>
              
              <button 
                v-else
                type="submit" 
                class="btn-wizard-submit-cozy"
                :disabled="isLoading"
              >
                <span v-if="isLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                <icon v-else icon="fa-solid fa-rocket" class="me-2" />
                <span>{{ isLoading ? 'Đang lưu...' : 'Hoàn tất & Khám phá ngay' }}</span>
              </button>
            </div>
          </div>
          
        </form>
      </div>
    </div>

    <!-- =============================================================
         LAYOUT 2: Settings View (Premium 2-Column Balanced Dashboard)
         ============================================================= -->
    <div v-else class="profile-form-inner-content animate-fade-in text-start">
      <!-- Page Header -->
      <div class="pref-page-header">
        <h4>
          <icon icon="fa-solid fa-wand-magic-sparkles" />
          Thiết lập gợi ý & Cá nhân hóa
        </h4>
        <p>Giúp Lối Đọc hiểu bạn hơn - gợi ý sách trúng gu, đúng cảm xúc và mục tiêu đọc của bạn.</p>
      </div>

      <form @submit.prevent="handleSave">
        <!-- TOP SECTION: FreeText & Categories side-by-side on desktop -->
        <div class="row g-4 mb-4">
          <!-- Column 1: Thoughts -->
          <div class="col-lg-6 col-12 d-flex">
            <div class="settings-pref-block w-100 p-4 rounded-4 bg-white border border-light-custom d-flex flex-column justify-content-between">
              <div>
                <label class="form-label font-serif fw-bold text-dark-custom mb-1 fs-7">Bạn đang nghĩ gì hôm nay?</label>
                <p class="text-muted-custom fs-8 mb-3">Viết ngắn gọn để AI tìm thấy cuốn sách thích hợp nhất cho bạn lúc này.</p>
              </div>
              <div class="pref-freetext-wrapper is-settings-freetext flex-grow-1 d-flex flex-column">
                <textarea
                  v-model="prefFreeText"
                  class="pref-freetext-area flex-grow-1"
                  placeholder="Hôm nay mình muốn đọc sách về..."
                  rows="4"
                  maxlength="300"
                  @input="autoResize"
                  ref="freetextArea"
                  style="min-height: 120px;"
                ></textarea>
                <div class="pref-freetext-footer mt-2">
                  <span class="pref-freetext-hint"><icon icon="fa-solid fa-circle-info" /> Tối đa 300 ký tự</span>
                  <span class="pref-freetext-count">{{ prefFreeText.length }}/300</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Column 2: Categories -->
          <div class="col-lg-6 col-12 d-flex">
            <div class="settings-pref-block w-100 p-4 rounded-4 bg-white border border-light-custom d-flex flex-column">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="form-label font-serif fw-bold text-dark-custom mb-0 fs-7">Thể loại yêu thích</label>
                <span class="badge-genre-counter is-settings-counter">{{ prefGenres.length }} / 3</span>
              </div>
              <p class="text-muted-custom fs-8 mb-3">Chọn nhiều nhất 3 thể loại sách bạn đặc biệt quan tâm.</p>

              <!-- Selected categories tags -->
              <div v-if="selectedGenres.length > 0" class="d-flex flex-wrap gap-2 mb-3">
                <span
                  v-for="opt in selectedGenres"
                  :key="opt.value"
                  class="pref-genre-selected-tag d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill"
                >
                  <icon :icon="opt.icon" class="pill-icon" style="font-size:0.7rem" />
                  <span class="fs-8">{{ opt.label }}</span>
                  <button
                    type="button"
                    class="border-0 bg-transparent p-0 ms-1 d-inline-flex"
                    style="color:inherit;cursor:pointer;line-height:1"
                    @click="prefGenres = prefGenres.filter(v => v !== opt.value)"
                  >
                    <icon icon="fa-solid fa-xmark" />
                  </button>
                </span>
              </div>

              <!-- Search categories input -->
              <div class="mb-3 genre-search-wrapper settings-search">
                <input
                  v-model="searchGenreQuery"
                  type="text"
                  class="genre-search-input-settings"
                  placeholder="Tìm kiếm thể loại..."
                />
                <icon icon="fa-solid fa-magnifying-glass" class="genre-search-icon-left" />
                <button
                  v-if="searchGenreQuery"
                  type="button"
                  class="genre-search-btn-clear"
                  @click="searchGenreQuery = ''"
                >
                  <icon icon="fa-solid fa-xmark" />
                </button>
              </div>

              <div class="pref-genre-pills-container d-flex flex-wrap gap-2 max-h-350-scroller flex-grow-1" style="overflow-y: auto;">
                <!-- Loading skeleton -->
                <template v-if="isGenresLoading">
                  <span v-for="n in 6" :key="n" class="pref-genre-pill-skeleton"></span>
                </template>
                <!-- Loaded -->
                <template v-else-if="filteredGenreOptions.length > 0">
                  <label
                    v-for="opt in filteredGenreOptions"
                    :key="opt.value"
                    class="pref-genre-pill-v2 d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill text-start position-relative is-settings-pill"
                    :class="{
                      'active': prefGenres.includes(opt.value),
                      'disabled-pill': prefGenres.length >= 3 && !prefGenres.includes(opt.value)
                    }"
                    style="cursor: pointer;"
                  >
                    <input
                      type="checkbox"
                      :value="opt.value"
                      v-model="prefGenres"
                      class="d-none"
                      :disabled="prefGenres.length >= 3 && !prefGenres.includes(opt.value)"
                    />
                    <icon :icon="opt.icon" class="pill-icon" :class="prefGenres.includes(opt.value) ? 'text-white' : 'text-primary'" />
                    <span class="fs-8 select-title">{{ opt.label }}</span>
                    <icon v-if="prefGenres.includes(opt.value)" icon="fa-solid fa-circle-check" class="pill-check text-white" />
                  </label>
                </template>
                <p v-else class="pref-genres-empty col-12 text-center py-3">Không tải được danh sách.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- BOTTOM SECTION: Moods, Styles & Goals (Balanced 3-column layout on Desktop) -->
        <div class="row g-4">
          <!-- Card 1: Mood -->
          <div class="col-xl-4 col-md-6 col-12">
            <div class="settings-pref-block p-4 rounded-4 bg-white border border-light-custom h-100 d-flex flex-column">
              <label class="form-label font-serif fw-bold text-dark-custom mb-1 fs-7">Tâm trạng hiện tại của bạn? 🌀</label>
              <p class="text-muted-custom fs-8 mb-3">Gợi ý tựa sách theo cảm xúc của bạn.</p>

              <div class="d-flex flex-column gap-2 flex-grow-1">
                <label
                  v-for="opt in moodOptions"
                  :key="opt.value"
                  class="pref-card-label d-flex align-items-center justify-content-between p-2.5 rounded-4 text-start position-relative is-settings-card"
                  :class="{ 'active': prefMood === opt.value }"
                  style="cursor: pointer;"
                >
                  <input type="radio" :value="opt.value" v-model="prefMood" class="d-none" />
                  <div class="d-flex align-items-center gap-2" style="flex: 1; min-width: 0;">
                    <div class="card-emoji-box is-settings-emoji-box" style="width: 36px; height: 36px; font-size: 1.15rem; flex-shrink: 0;">{{ opt.emoji }}</div>
                    <span class="fw-bold fs-8 select-title text-dark-custom">{{ opt.label }}</span>
                  </div>
                  <icon v-if="prefMood === opt.value" icon="fa-solid fa-circle-check" class="check-icon-settings text-primary flex-shrink-0" />
                </label>
              </div>
            </div>
          </div>

          <!-- Card 2: Book style -->
          <div class="col-xl-4 col-md-6 col-12">
            <div class="settings-pref-block p-4 rounded-4 bg-white border border-light-custom h-100 d-flex flex-column">
              <label class="form-label font-serif fw-bold text-dark-custom mb-1 fs-7">Phong cách đọc ưa thích? 🏆</label>
              <p class="text-muted-custom fs-8 mb-3">Hình thức và độ dài sách tiếp cận.</p>

              <div class="d-flex flex-column gap-2 flex-grow-1">
                <label
                  v-for="opt in bookTypeOptions"
                  :key="opt.value"
                  class="pref-card-label d-flex align-items-center justify-content-between p-2.5 rounded-4 text-start position-relative is-settings-card"
                  :class="{ 'active': prefBookType === opt.value }"
                  style="cursor: pointer;"
                >
                  <input type="radio" :value="opt.value" v-model="prefBookType" class="d-none" />
                  <div class="d-flex align-items-center gap-2" style="flex: 1; min-width: 0;">
                    <div class="card-emoji-box is-settings-emoji-box" style="width: 36px; height: 36px; font-size: 1.15rem; flex-shrink: 0;">{{ opt.emoji }}</div>
                    <span class="fw-bold fs-8 select-title text-dark-custom">{{ opt.label }}</span>
                  </div>
                  <icon v-if="prefBookType === opt.value" icon="fa-solid fa-circle-check" class="check-icon-settings text-primary flex-shrink-0" />
                </label>
              </div>
            </div>
          </div>

          <!-- Card 3: Goal -->
          <div class="col-xl-4 col-12">
            <div class="settings-pref-block p-4 rounded-4 bg-white border border-light-custom h-100 d-flex flex-column">
              <label class="form-label font-serif fw-bold text-dark-custom mb-1 fs-7">Mục tiêu đọc sách? 🚀</label>
              <p class="text-muted-custom fs-8 mb-3">Đích đến trong hành trình đọc của bạn.</p>

              <div class="d-flex flex-column gap-2 flex-grow-1">
                <label
                  v-for="opt in goalOptions"
                  :key="opt.value"
                  class="pref-card-label d-flex align-items-center justify-content-between p-2.5 rounded-4 text-start position-relative is-settings-card"
                  :class="{ 'active': prefGoal === opt.value }"
                  style="cursor: pointer;"
                >
                  <input type="radio" :value="opt.value" v-model="prefGoal" class="d-none" />
                  <div class="d-flex align-items-center gap-2" style="flex: 1; min-width: 0;">
                    <div class="card-emoji-box is-settings-emoji-box" style="width: 36px; height: 36px; font-size: 1.15rem; flex-shrink: 0;">{{ opt.emoji }}</div>
                    <span class="fw-bold fs-8 select-title text-dark-custom">{{ opt.label }}</span>
                  </div>
                  <icon v-if="prefGoal === opt.value" icon="fa-solid fa-circle-check" class="check-icon-settings text-primary flex-shrink-0" />
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- SAVE BUTTON IN SETTINGS -->
        <div class="pref-save-footer">
          <button
            type="submit"
            class="is-settings-btn-submit"
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <icon v-else icon="fa-solid fa-floppy-disk" />
            <span>{{ isLoading ? 'Đang lưu...' : 'Lưu sở thích' }}</span>
          </button>
        </div>
      </form>
    </div>

  </div>
</template>

<script src="./search-preferences.js"></script>
<style scoped src="./search-preferences.css"></style>
